import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react'
import { getRegistry, type WidgetRegistry, type WidgetRegistrySnapshot } from '../registry'

const RegistryContext = createContext<WidgetRegistry | null>(null)

export interface RegistryProviderProps {
  registry?: WidgetRegistry
  children: ReactNode
}

export function RegistryProvider({ registry, children }: RegistryProviderProps) {
  const instance = registry ?? getRegistry()
  return (
    <RegistryContext.Provider value={instance}>
      {children}
    </RegistryContext.Provider>
  )
}

export function useRegistry(): WidgetRegistry {
  const ctx = useContext(RegistryContext)
  if (ctx) return ctx
  // Fallback to the singleton so callers outside a RegistryProvider still function (rare;
  // most apps use <ReqdeskProvider> which wraps this).
  return getRegistry()
}

export function useRegistrySnapshot<T>(selector: (snapshot: WidgetRegistrySnapshot) => T): T {
  const registry = useRegistry()
  return useSyncExternalStore(
    registry.subscribe,
    () => selector(registry.getSnapshot()),
    () => selector(registry.getSnapshot()),
  )
}
